import { useMemo, useRef, useState } from 'react';
import { UMR } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { FamilyNav } from './FamilyNav';
import { FamilyTiles } from './FamilyTiles';
import { MapPane, type MapOverlay } from './MapPane';
import { ParamsTab } from './ParamsTab';
import { PlanDevPanel } from './PlanDevPanel';
import { SelectionsPanel } from './SelectionsPanel';
import { SectionRail } from './SectionRail';
import { SetEditor } from './SetEditor';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { FAMILIES, type AnySet, type Draft, type Family, type PlanBrick, type Project, type Tab, type TechnicalPoint } from './types';
import type { CalculationSection } from './sectionEngine';
import { TechnicalWorkspace } from './TechnicalWorkspace';
import { ProjectExchange } from './ProjectExchange';
import { SaveBar } from './ui';

export interface ProjectViewProps {
  project: Project;
  geo: PlanGeoData | undefined;
  tab: Tab;
  family?: Family;
  setId: string;
  edit: boolean;
  planId: string;
  draft: Draft | null;
  canWrite: boolean;
  planDraft: Project['plans'][number] | null;
  onPlanDraftChange: (plan: Project['plans'][number] | null) => void;
  onTab: (t: Tab) => void;
  onOpenSet: (family: Family, id: string, edit: boolean) => void;
  onBackOverview: () => void;
  onSelectFamily: (f: Family) => void;
  onSelectSet: (id: string) => void;
  onModify: () => void;
  onConsult: () => void;
  onCreateSet: (family: Family) => void;
  onDuplicateSet: (family: Family, id: string) => void;
  onDeleteSet: (family: Family, id: string) => void;
  onSetChange: (s: AnySet) => void;
  onSave: () => void;
  onCancel: () => void;
  onSelectPlan: (id: string) => void;
  onRun: (id: string, section: CalculationSection) => void;
  technicalDraft: TechnicalPoint[] | null;
  onTechnicalDraft: (points: TechnicalPoint[] | null) => void;
  onConfirmTechnical: () => void;
  onCreatePlan: (name: string, energy: Project['plans'][number]['energy']) => string;
  onDuplicatePlan: (id: string) => string;
  onUpdatePlan: (id: string, changes: Partial<Project['plans'][number]>) => void;
  onCreateSelection: (name: string, buildingIds: string[]) => string;
  onUpdateSelection: (id: string, changes: { name?: string; buildingIds?: string[] }) => void;
  onDeleteSelection: (id: string) => void;
}

const BASE_LEGEND = 'Carte : copie Gentilly (proposition-c) — bâtiments colorés par consommation, réseaux existants en trait plein';
const PANEL_W = 440;

/** Rend la carte à droite d'un panneau latéral (familles « carte » et plans). */
function WithMap({ geo, panel, overlay }: { geo: PlanGeoData | undefined; panel: JSX.Element; overlay: MapOverlay }) {
  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <aside style={{ flex: `0 0 ${PANEL_W}px`, overflowY: 'auto', borderRight: `1px solid ${UMR.border}`, background: UMR.white }}>{panel}</aside>
      {geo ? <MapPane geo={geo} overlay={overlay} /> : <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: UMR.textSecondary }}>Chargement de la carte…</div>}
    </div>
  );
}

/**
 * Zone de travail d'un projet. Deux niveaux de navigation :
 *  (1) rail de sections figé à gauche (Hypothèses / Plans / Paramètres) ;
 *  (2) dans Hypothèses : accueil en tuiles puis liste compacte de familles.
 * La carte n'apparaît qu'à la demande (Tracé, Importance, Points de départ, et résultats des plans).
 */
export function ProjectView(p: ProjectViewProps) {
  const [brick, setBrick] = useState<PlanBrick>('buildings');
  const [returnContext, setReturnContext] = useState<{ planId: string; brick: PlanBrick } | null>(null);
  const [exportPlanId, setExportPlanId] = useState('');
  const [showSST, setShowSST] = useState(false);
  const { project, tab, family } = p;
  const sets = family ? (project.sets[family] as AnySet[]) : [];
  const set = family ? sets.find((s) => s.id === p.setId) ?? sets[0] : undefined;
  const setValue: AnySet | undefined = p.draft && set && p.draft.value.id === set.id ? p.draft.value : set;
  const plan = project.plans.find((x) => x.id === p.planId) ?? project.plans[0];
  const familyMeta = family ? FAMILIES.find((f) => f.key === family) : undefined;

  const cb = useRef(p.onSetChange);
  cb.current = p.onSetChange;
  const connectedIds = tab === 'plans' ? plan?.results?.connectedIds : undefined;

  const overlay = useMemo<MapOverlay>(() => {
    if (tab === 'plans') {
      return { connected: connectedIds ? new Set(connectedIds) : undefined, legend: [BASE_LEGEND, connectedIds ? '■ Violet : bâtiments raccordés par le dernier calcul du plan' : 'Lancez le calcul d’un plan pour afficher ses bâtiments raccordés'] };
    }
    const editable = p.edit;
    if (setValue?.kind === 'trace') {
      return { forbiddenRoads: setValue.forbiddenRoads, mandatoryRoads: setValue.mandatoryRoads, weightedRoads: setValue.weightedRoads, legend: [BASE_LEGEND, '▬ Rouge : interdites · Vert : obligatoires · Jaune : pondérées (mock)'] };
    }
    if (setValue?.kind === 'importance') {
      const m = setValue.mandatoryBuildings;
      const toggle = editable ? (fid: string) => cb.current({ ...setValue, mandatoryBuildings: m.includes(fid) ? m.filter((x) => x !== fid) : [...m, fid] }) : undefined;
      return { mandatory: m, onBuildingClick: toggle, legend: [BASE_LEGEND, `■ Contour violet : ${m.length} bâtiment(s) obligatoire(s)${editable ? ' — cliquez un bâtiment pour l’ajouter / le retirer' : ''}`] };
    }
    if (setValue?.kind === 'pointsDepart') {
      return { startPoints: setValue.startPoints, legend: [BASE_LEGEND, `● Vert : ${setValue.startPoints} point(s) de départ du réseau (mock)`] };
    }
    return { legend: [BASE_LEGEND] };
  }, [tab, p.edit, connectedIds, setValue]);

  // ─── Section : Plans de dev (carte = résultats) ───
  if (tab === 'plans') {
    return (
      <Shell project={project} tab={tab} onTab={p.onTab}>
        <PlanDevPanel
          project={project} geo={p.geo} canWrite={p.canWrite} selectedId={plan?.id ?? ''}
          onSelect={p.onSelectPlan} onCreate={p.onCreatePlan} onDuplicate={p.onDuplicatePlan}
          onUpdate={p.onUpdatePlan} onRun={p.onRun} onOpenSet={(f, id) => {
            setReturnContext({ planId: plan?.id ?? '', brick });
            if (id === '__create') p.onCreateSet(f);
            else p.onOpenSet(f, id, false);
          }}
          draftPlan={p.planDraft} onDraftChange={p.onPlanDraftChange}
          active={brick} onBrick={setBrick}
          onSST={() => { setShowSST(true); p.onTab('params'); }}
          onExport={() => { setExportPlanId(plan?.id ?? ''); p.onTab('exchange'); }}
        />
      </Shell>
    );
  }

  if (tab === 'selections') {
    return (
      <Shell project={project} tab={tab} onTab={p.onTab}>
        <SelectionsPanel
          project={project} activePlanId={p.planId} geo={p.geo} canWrite={p.canWrite} onCreate={p.onCreateSelection}
          onUpdate={p.onUpdateSelection} onDelete={p.onDeleteSelection}
        />
      </Shell>
    );
  }

  if (tab === 'exchange') return <Shell project={project} tab={tab} onTab={p.onTab}>
    <ProjectExchange project={project} geo={p.geo} initialPlanId={exportPlanId} canWrite={p.canWrite}
      draft={p.planDraft} onDraft={p.onPlanDraftChange}
      onUpdate={p.onUpdatePlan} onSST={() => { setShowSST(true); p.onTab('params'); }} />
  </Shell>;

  if (tab === 'optimization') {
    return (
      <Shell project={project} tab={tab} onTab={p.onTab}>
        <div style={{ padding: 32, maxWidth: 760 }}>
          <NJText as="h2" scale="lg" style={{ margin: '0 0 8px' }}>Optimisation <span style={{ color: UMR.textSecondary, fontSize: 14 }}>V2 · À venir</span></NJText>
          <NJText scale="md">À partir d’une Sélection et des hypothèses, l’algorithme proposera plusieurs plans candidats. Vous pourrez comparer consommation et longueur de réseau, puis promouvoir un candidat en Plan de dev.</NJText>
          <NJText scale="md">Cette fonction est désactivée dans la V1. Les Sélections du projet seront réutilisées ici, sans nouvelle curation.</NJText>
        </div>
      </Shell>
    );
  }

  // ─── Section : Paramètres (pas de carte) ───
  if (tab === 'params') {
    return (
      <Shell project={project} tab={tab} onTab={p.onTab}>
        <div style={{ display: 'flex', gap: 8, padding: 8 }}>
          <NJButton label="Paramètres du projet" variant={!showSST ? 'primary' : 'secondary'} scale="sm" onClick={() => setShowSST(false)} />
          <NJButton label="SST / locaux techniques projet" variant={showSST ? 'primary' : 'secondary'} scale="sm" onClick={() => setShowSST(true)} />
          {showSST && p.canWrite && <SaveBar dirty={p.technicalDraft !== null}
            onCancel={() => p.onTechnicalDraft(null)} onSave={p.onConfirmTechnical} />}
        </div>
        {showSST && plan ? <TechnicalWorkspace plan={{ ...plan, technicalPoints: p.technicalDraft ?? project.technicalPoints ?? [] }}
          geo={p.geo} canWrite={p.canWrite} onUpdate={(changes) => p.onTechnicalDraft(changes.technicalPoints ?? [])} />
          : <div style={{ flex: 1, overflowY: 'auto' }}><ParamsTab project={project} /></div>}
      </Shell>
    );
  }

  // ─── Section : Hypothèses, accueil en tuiles (pas de carte) ───
  if (!family || !set || !familyMeta) {
    return (
      <Shell project={project} tab={tab} onTab={p.onTab}>
        <FamilyTiles project={project} onEnterFamily={p.onSelectFamily} />
      </Shell>
    );
  }

  // ─── Section : Hypothèses, dans une famille (liste compacte + éditeur + carte à la demande) ───
  const editor = (
    <SetEditor
      project={project} family={family} selectedId={set.id} edit={p.edit && p.canWrite} canWrite={p.canWrite} draft={p.draft?.value ?? null}
      onSelectSet={p.onSelectSet} onModify={p.onModify} onConsult={p.onConsult}
      onCreate={() => p.onCreateSet(family)} onDuplicate={(id) => p.onDuplicateSet(family, id)} onDelete={(id) => p.onDeleteSet(family, id)}
      onChange={p.onSetChange} onSave={p.onSave} onCancel={p.onCancel} onOpenPlan={p.onSelectPlan}
    />
  );
  const hasMap = familyMeta.map !== 'none';

  return (
    <Shell project={project} tab={tab} onTab={p.onTab}>
      {returnContext && <div style={{ padding: 8, borderBottom: `1px solid ${UMR.border}` }}>
        <NJButton label={`Retour au plan · ${returnContext.brick === 'buildings' ? 'Bâtiments' : returnContext.brick === 'trace' ? 'Tracé' : 'Phasage'}`}
          variant="secondary" scale="sm" onClick={() => {
            setBrick(returnContext.brick); p.onSelectPlan(returnContext.planId);
          }} />
        <NJText scale="xs" style={{ display: 'inline', marginLeft: 12 }}>Le jeu créé se choisit au retour, directement dans l’étape.</NJText>
      </div>}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <FamilyNav project={project} active={family} onSelectFamily={p.onSelectFamily} onHome={p.onBackOverview} />
        {hasMap
          ? <WithMap geo={p.geo} panel={editor} overlay={overlay} />
          : <div style={{ flex: 1, overflowY: 'auto', background: UMR.white }}>{editor}</div>}
      </div>
    </Shell>
  );
}

/** Rail de sections figé + zone de contenu. */
function Shell({ project, tab, onTab, children }: { project: Project; tab: Tab; onTab: (t: Tab) => void; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <SectionRail project={project} tab={tab} onChange={onTab} />
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}
